<template>
    <b-container fluid class="mt-2">
        <b-row>
            <b-col class="d-flex align-items-center flex-wrap">
                <span class="font-size-1rem mr-4">Account is in {{ insuranceAccountMode }}</span>
                <b-button variant="outline-secondary" @click="toggleInsuranceAccount">
                    {{ insuranceTestMode ? 'ISSUE TEST POLICIES' : 'ISSUE LIVE POLICIES' }}
                </b-button>
            </b-col>
        </b-row>
        <b-row class="mt-4">
            <b-col md="5">
                <validation-observer ref="form" v-slot="{ meta, handleSubmit, setFieldValue, errors }">
                    <b-form @submit.prevent="handleSubmit(_, save)">
                        <validation-provider name="claims_correspondence_email" rules="email">
                            <form-input
                                floating-label="Claims correspondence email"
                                v-model="claimsCorrespondenceEmail"
                                :left-icon="['fas', 'envelope']"
                                :error="errors.claims_correspondence_email"
                                @input="(val) => setFieldValue('claims_correspondence_email', val)"
                            ></form-input>
                        </validation-provider>
                        <b-button class="mb-2" type="submit" variant="primary" :disabled="loading || meta.valid">
                            Save
                        </b-button>
                    </b-form>
                </validation-observer>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <div class="font-weight-bold mt-4">
                    Damage protection fees are configured on the
                    <a href="/app/property-manager/damage/">Deposit / Fees / Services / Taxes interface</a>
                </div>
            </b-col>
        </b-row>
        <modal
            :id="trainingModalID"
            title="Training Required"
            ok-title="Contact Me About Training"
            @ok="sendIntercomTrainingRequest"
        >
            Training is required to offer the Security Deposit Waiver to your guests. Click the "Contact Me About
            Training" button below and someone from Lodgix will be in touch shortly to schedule training.
        </modal>
    </b-container>
</template>

<script>
    import API from '../../../api';
    import {
        DAMAGE_PROTECTION_CLAIMS_CORRESPONDENCE_EMAIL_ENDPOINT,
        DAMAGE_PROTECTION_METADATA_ENDPOINT,
        DAMAGE_PROTECTION_TOGGLE,
        DAMAGE_PROTECTION_SEND_INTERCOM_TRAINING_REQUEST,
    } from '../../../endpoints';
    import { formatResponseError } from '../../../utils/api';
    import FormInput from '../../generic/form-input/form-input.vue';
    import Modal from '../../generic/modal/modal.vue';

    export default {
        name: 'DamageProtectionSetup',
        components: {
            FormInput,
            Modal,
        },
        data() {
            return {
                loading: false,
                claimsCorrespondenceEmail: '',
                insuranceAccountMode: '',
                insuranceTestMode: false,
                trainingModalID: 'damage-protection-setup-training-modal',
            };
        },
        mounted() {
            (async () => {
                this.$parent.$emit('busy', true);
                try {
                    await this.fetchMetadata();
                } finally {
                    this.$parent.$emit('busy', false);
                }
            })();
        },
        methods: {
            async fetchMetadata() {
                try {
                    const response = await API.get(DAMAGE_PROTECTION_METADATA_ENDPOINT());
                    this.insuranceAccountMode = response.data.insurance_account_mode;
                    this.insuranceTestMode = response.data.insurance_test_mode;
                    this.claimsCorrespondenceEmail = response.data.claims_correspondence_email;
                } catch (err) {
                    this.errorToast({ text: 'Could not load meta data.' });
                }
            },
            async save() {
                this.loading = true;

                try {
                    await API.put(DAMAGE_PROTECTION_CLAIMS_CORRESPONDENCE_EMAIL_ENDPOINT(), {
                        claims_correspondence_email: this.claimsCorrespondenceEmail,
                    });
                    this.successToast({ text: 'Claims correspondence email successfully updated.' });
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({ text: generic });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);
                    }
                } finally {
                    this.loading = false;
                }
            },
            async sendIntercomTrainingRequest() {
                this.$parent.$emit('busy', true);

                try {
                    await API.post(DAMAGE_PROTECTION_SEND_INTERCOM_TRAINING_REQUEST());
                    this.successToast({ text: 'Damage Protection training successfully requested.' });
                } catch (err) {
                    const { generic } = formatResponseError(err);

                    this.errorToast({ text: generic || 'Could not send training request.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async toggleInsuranceAccount() {
                try {
                    const response = await API.post(DAMAGE_PROTECTION_TOGGLE());
                    const status = response.data.status;
                    if (status === 'ACTIVATED') {
                        this.successToast({ text: 'Damage Protection successfully activated.' });
                        await this.fetchMetadata();
                    } else if (status === 'DEACTIVATED') {
                        this.successToast({ text: 'Damage Protection successfully deactivated.' });
                        await this.fetchMetadata();
                    } else if (status === 'TRAINING') {
                        this.$bvModal.show(this.trainingModalID);
                    }
                } catch {
                    this.errorToast({ text: 'Could not toggle damage protection.' });
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .font-size-1rem {
        font-size: 1rem;
    }
</style>
